/**
 * <s-sound-video> — Portrait video card that autoplays muted and lets the
 * shopper turn sound on and pause/play.
 *
 * Commands (from descendant buttons via commandfor/command):
 *   --toggle-sound  unmute/mute (unmuting also resumes playback)
 *   --toggle-play   pause/play
 *
 * State attributes on the host: [data-playing], [data-sound].
 * Events: s-sound-video:unmute (bubbles) when sound turns on, so a parent
 * carousel can mute the other cards. Public method: mute().
 */
class SSoundVideo extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('command', this.#handleCommand, { signal });
    const video = this.#video();
    if (!video) return;
    video.addEventListener('play', () => this.toggleAttribute('data-playing', true), { signal });
    video.addEventListener('pause', () => this.removeAttribute('data-playing'), { signal });
    video.addEventListener('volumechange', () => this.#syncSound(), { signal });
    if (video.paused) this.removeAttribute('data-playing');
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  mute() {
    const video = this.#video();
    if (video) video.muted = true;
  }

  #video() {
    return this.querySelector('video');
  }

  #syncSound() {
    const video = this.#video();
    const on = Boolean(video && !video.muted);
    this.toggleAttribute('data-sound', on);
    this.querySelector('[command="--toggle-sound"]')?.setAttribute('aria-pressed', String(on));
  }

  #handleCommand = (event) => {
    const video = this.#video();
    if (!video) return;
    if (event.command === '--toggle-sound') {
      video.muted = !video.muted;
      if (!video.muted) {
        if (video.volume === 0) video.volume = 1;
        video.play().catch(() => {});
        this.dispatchEvent(new CustomEvent('s-sound-video:unmute', { bubbles: true }));
      }
    } else if (event.command === '--toggle-play') {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    }
  };
}

if (!customElements.get('s-sound-video')) customElements.define('s-sound-video', SSoundVideo);
